// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // Starter Custom Code
    list?: TypographyPresentationProps;
    item?: TypographyPresentationProps;
    noResult?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    attributeTypeLabelText: {
        weight: "inherit",
        css: css`
            width: calc(50% - 0.5em);
            font-weight: ${x => x.theme.design.typography.weight.bold};
            font-size: ${x => x.theme.design.typography.size.base}; /* Store Custom Code */
        `,
    },
    attributeTypeValuesText: {
        css: css`
            width: calc(50% - 0.5em);
            font-size: ${x => x.theme.design.typography.size.base}; /* Store Custom Code */
        `,
    },
    list: {
        size: "inherit",
        css: css`
            display: flex;
            flex-flow: row wrap;
            justify-content: space-between;
            font-size: ${x => x.theme.design.typography.size.smaller}; /* Store Custom Code */
            word-wrap: break-word;
        `,
    },
    item: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            padding: ${pxToRem(12, 16)};
            background-color: ${getColor("common.background")};

            ${breakpoint("sm", "down")} {
                &:nth-child(odd) {
                    background-color: ${getColor("common.accent")};
                }
            }

            ${breakpoint("md")} {
                width: calc(50% - 10px);
                padding: ${pxToRem(14, 16)};

                &:nth-child(4n + 1),
                &:nth-child(4n + 2) {
                    background-color: ${getColor("common.accent")};
                }
            }

            ${breakpoint("xl")} {
                padding: ${pxToRem(16, 18)};
            }
        `,
    },
    noResult: {
        forwardAs: "p",
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        /* Starter Custom Code - Start */
        return <Typography {...styles.noResult}>{translate("There are no specifications available.")}</Typography>;
        /* Starter Custom Code - End */
    }
    /* Starter Removed - limitedAttributeTypes */

    /* Starter Custom Code - Start */
    return (
        <Typography as="ul" {...styles.list} data-test-selector="productDetails_attributes">
            {brand && (
                <Typography as="li" data-test-selector="brand_item" {...styles.item}>
                    <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                        {translate("Brand")}
                    </Typography>
                    <Typography data-test-selector="brand_item_value" {...styles.attributeTypeValuesText}>
                        {brand.name}
                    </Typography>
                </Typography>
            )}
            {attributeTypes &&
                attributeTypes.map(attributeType => (
                    <Typography
                        as="li"
                        key={attributeType.id.toString()}
                        data-test-selector="attributes_item"
                        data-attributetypeid={attributeType.id}
                        {...styles.item}
                    >
                        <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                            {attributeType.label || attributeType.name}
                        </Typography>
                        <Typography data-test-selector="attributes_item_value" {...styles.attributeTypeValuesText}>
                            {(attributeType.attributeValues ?? [])
                                .map(attributeValue => attributeValue.valueDisplay)
                                .join(", ")}
                        </Typography>
                    </Typography>
                ))}
        </Typography>
    );
    /* Starter Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
